import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Props } from 'react-modal';
import WebApp from '@twa-dev/sdk';
import {
  FlexRow,
  Text,
  FilledButton,
  StyledSvg,
  useTextFormatter
} from '@make-software/tg-mini-app-ui';

import {
  BaseModal,
  ButtonWrapper,
  CloseIconButton,
  EarnArea,
  IconWrapper,
  ModalContentWrapper,
  ModalDescription,
  StyledContent,
  StyledHeader
} from '@/shared';
import CloseIcon from '@/shared/assets/icons/ic-close.svg';
import InviteFriend from '@/shared/assets/icons/ic-invite-friend.svg';
import GreenCheckmark from '@/shared/assets/icons/ic-green-checkmark.svg';
import GoldIcon from '@/shared/assets/icons/gold.svg';

import { StyledBodyText } from './invite-friends-modal.styled';

function getInviteLink(userId: string): string {
  // Generate link to the app's auth page with friendID parameter
  // This allows users to choose between Telegram or Discord authentication
  const appUrl = window.location.origin;
  return `${appUrl}/?friendID=${userId}`;
}

export interface InviteFriendsModalProps extends Props {
  isOpen: boolean;
  onCloseModal?: () => void;
  userId: string;
}

export const InviteFriendsModal = ({ isOpen, onCloseModal, userId }: InviteFriendsModalProps) => {
  const theme = useTheme();
  const formatter = useTextFormatter();
  const [isCopied, setIsCopied] = useState(false); // Track if the text has been copied
  const friendshipReward = formatter.formatPoints(
    parseInt(window.config.season_info.friendship_reward, 10)
  );

  const closeDrawer = () => {
    onCloseModal?.();
  };

  const handleInviteFriend = async () => {
    try {
      const inviteLink = getInviteLink(userId);
      const shareText = 'Join CSPR.fans!';
      const o = {
        title: 'CSPR.fans',
        text: shareText,
        url: inviteLink
      };
      if (navigator.share !== undefined && navigator.canShare(o)) {
        await navigator.share(o);
      } else {
        WebApp.openTelegramLink(
          `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(shareText)}`
        );
      }
    } catch (err: any) {
      console.error(err.message);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(getInviteLink(userId))
      .then(() => {
        setIsCopied(true);
      })
      .catch(() => {
        WebApp.showAlert('Could not copy the invite link.');
      });
  };

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false); // Reset the copied state
      }, 2000);

      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [isCopied]);

  return isOpen ? (
    <BaseModal
      onRequestClose={closeDrawer}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      style={{
        content: {
          inset: 'auto 0 0 0',
          padding: '0 0 20px 0',
          boxShadow: 'inset 0 0 0 1px #41424E',
          top: 'auto'
        }
      }}
    >
      <ModalContentWrapper direction={'column'}>
        <StyledHeader align={'center'} justify={'space-between'}>
          <FlexRow gap={8} align={'center'}>
            <IconWrapper>
              <StyledSvg src={InviteFriend} size={20} fillColor="#E1E1E1" />
            </IconWrapper>
            <StyledBodyText weight={1} size="md">
              Invite friends
            </StyledBodyText>
          </FlexRow>
          <CloseIconButton
            iconSrc={CloseIcon}
            onClick={closeDrawer}
            size={35}
            backgroundColor={'#30313B'}
            borderColor={'#30313B'}
            borderRadius="50%"
            iconColor="white"
          />
        </StyledHeader>
        <StyledContent>
          <EarnArea>
            Earn:{' '}
            <FlexRow align={'center'} gap={'5px'}>
              <StyledSvg src={GoldIcon} fillColor="#F1BF0B" />{' '}
              <span>
                <Text weight={3} monotype>
                  {friendshipReward}
                </Text>
              </span>
            </FlexRow>
          </EarnArea>
          <ModalDescription>
            Being a Casper Fan is more fun with your friends! Invite them to join you, and for every
            Casper Fan you bring in, you will earn{' '}
            {formatter.formatPoints(parseInt(window.config.season_info.friendship_reward, 10))} fan
            points!
          </ModalDescription>
        </StyledContent>
      </ModalContentWrapper>
      <ButtonWrapper direction={'column'}>
        <FilledButton
          fontFamily={'Inter'}
          title={
            <FlexRow align={'center'} gap={'16px'}>
              <span>Invite Friend</span>

            </FlexRow>
          }
          onClick={handleInviteFriend}
          base={{ backgroundColor: theme.colors.filledBtn, color: '#FFF', border: '0px' }}
          borderRadius="4px"
          hover={{backgroundColor: theme.colors.filledBtnHover, border: '0px'}}
        />
        <FilledButton
          fontFamily={'Inter'}
          title={
            isCopied ? (
              <>
                <StyledSvg
                  src={GreenCheckmark}
                  size={16}
                  fillColor="#5BFFE1"
                  style={{ marginRight: '8px' }}
                />
                {'Copied!'}
              </>
            ) : (
              'Copy Link'
            )
          }
          onClick={handleCopyLink}
          base={{ backgroundColor: '#30313B', color: '#FFF', border: '0px' }}
          borderRadius="4px"
          hover={{backgroundColor: theme.colors.filledBtnHover, border: '0px'}}
        />
      </ButtonWrapper>
    </BaseModal>
  ) : null;
};
